import { Slider } from "@eworkit/iris";
import React, { FunctionComponent, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { Header } from "@/components/Header";
import { Section } from "@/components/Section";
import { Text } from "@/components/Text";
import { MAX_WORKSTYLES_POINTS_FOR_EACH } from "@/data/consultant-profiles";
import { Flex } from "@/eworkitComponents";

import "./_workstyle.scss";

interface ISingleWorkStyleEditProps {
  type: string;
  score: number;
  pointsLeft: number;
  WorkstyleIcon: any;
  onChange: any;
}

export const SingleWorkStyleEdit: FunctionComponent<ISingleWorkStyleEditProps> = ({
  type,
  score,
  WorkstyleIcon,
  onChange,
  pointsLeft,
}) => {
  const { t } = useTranslation("session");
  const [currentValue, setCurrentValue] = useState(score);
  const currentPointsLeft = useRef(pointsLeft);
  currentPointsLeft.current = pointsLeft;

  const handleChange = useCallback(
    (pts: number) => {
      setCurrentValue((currValue: number) => {
        if (pts > score && pointsLeft - (pts - score) < 0) {
          return Math.max(currValue, pointsLeft);
        }

        return pts;
      });
    },
    [pointsLeft, score],
  );

  const handleAfterChange = useCallback(
    (pts: number) => {
      if (pts !== score && pointsLeft - (pts - score) < 0) {
        const underLimit = pointsLeft - (pts - score);
        setCurrentValue(pts + underLimit);
        onChange(pts + underLimit);
      } else {
        onChange(pts);
      }
    },
    [score, pointsLeft],
  );

  return (
    <Section shadow="none" borderRadius border={[true, true, true, true]} key={type}>
      <Flex padding={["md"]} innerPadding="md" direction="row">
        <WorkstyleIcon size="md" color="vg500" />

        <Flex direction="column" innerPadding="md" style={{ width: "100%" }}>
          <Flex direction="row" justify="space-between">
            <Header size="sm">{t(`CONSULTANT_PROFILES.DETAIL.WORK_STYLES.TYPES.${type}.HEADER`)}</Header>
            <Badge
              borderRadius="4px"
              backgroundColor="background-card-bottom"
              color="grey1"
              borderColor="background-card-bottom"
              contentMargin={[false, "md"]}>
              {t(`CONSULTANT_PROFILES.DETAIL.WORK_STYLES.POINTS`, { count: currentValue })}
            </Badge>
          </Flex>
          <Text color="grey2">{t(`CONSULTANT_PROFILES.DETAIL.WORK_STYLES.TYPES.${type}.DESCRIPTION`)}</Text>
          <Flex>
            <Slider
              min={0}
              max={MAX_WORKSTYLES_POINTS_FOR_EACH}
              value={currentValue}
              dots={true}
              onChange={handleChange}
              onAfterChange={handleAfterChange}
            />
          </Flex>
        </Flex>
      </Flex>
    </Section>
  );
};
